import { ReactNode, useId } from 'react';

import { InputAffixLabel, InputContainer, InputElement } from './Input.styled';
import {
  InputAffixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.types';
import { InputProps } from './InputProps';

type Props = {
  inputProps: InputProps;
  defaultValue?: string | number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: InputStatus;
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
  fullWidth?: boolean;
};

export function Input({
  inputProps,
  defaultValue,
  prefix,
  suffix,
  status = 'neutral',
  affixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
  fullWidth,
}: Props) {
  const generatedId = `input-group-${useId()}`;
  const inputId = inputProps.id || generatedId;

  return (
    <InputContainer
      $status={status}
      $affixVariant={affixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
      $fullWidth={Boolean(fullWidth)}
    >
      {prefix && <InputAffixLabel htmlFor={inputId}>{prefix}</InputAffixLabel>}

      <InputElement
        aria-activedescendant={inputProps.ariaActiveDescendant}
        aria-autocomplete={inputProps.ariaAutocomplete}
        aria-controls={inputProps.ariaControls}
        aria-invalid={inputProps.ariaInvalid}
        aria-label={inputProps.ariaLabel}
        autoFocus={inputProps.autoFocus}
        data-modal-focus={inputProps.dataModalFocus}
        data-qa-id={inputProps.qaId}
        defaultValue={defaultValue}
        disabled={inputProps.disabled}
        id={inputId}
        max={inputProps.max}
        min={inputProps.min}
        name={inputProps.name}
        onBlur={inputProps.onBlur}
        onChange={inputProps.onChange}
        onClick={inputProps.onClick}
        onFocus={inputProps.onFocus}
        onKeyDown={inputProps.onKeyDown}
        onKeyPress={inputProps.onKeyPress}
        onKeyUp={inputProps.onKeyUp}
        placeholder={inputProps.placeholder}
        readOnly={inputProps.readOnly}
        ref={inputProps.ref}
        step={inputProps.step}
        title={inputProps.title}
        type={inputProps.type}
        inputMode={inputProps.inputMode}
        value={inputProps.value}
      />

      {suffix && <InputAffixLabel htmlFor={inputId}>{suffix}</InputAffixLabel>}
    </InputContainer>
  );
}
